import { DataResponse } from "shared/models";
import { baseQuery } from "./baseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";

export const customerService = createApi({
  reducerPath: "customerService",
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getCustomers: builder.query<
      DataResponse<any[]>,
      {
        page: number;
        limit: number;
        search: string;
        skip_log?: boolean;
        sortBy?: string;
        order?: string;
        merchant_id: number;
      }
    >({
      query: (params) => ({
        url: `/customer-invoice?merchant_id=${params?.merchant_id}&page=${params?.page}&limit=${params?.limit}&customer_search=${
          params?.search
        }&skip_log=${
          params?.skip_log ? params?.skip_log : false
        }${
          params?.sortBy ? `&sortBy=${params?.sortBy}` : ""
        }${params?.order ? `&order=${params?.order}` : ""}`,
        method: "GET",
        headers: !params?.skip_log
          ? {
            PageName: "Customer Management",
          }
          : {},
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success === true) {
          const data = rawData?.data.map((raw: any) => ({
            value: raw.id,
            ...raw,
          }));

          return { ...rawData, data };
        }
      },
    }),
    getInvoices: builder.query<
      DataResponse<any[]>,
      {
        page: number;
        limit: number;
        search: string;
        skip_log?: boolean;
        sortBy?: string;
        order?: string;
        customer_id?: number;
      }
    >({
      query: (params) => ({
        url: `/invoice?customer_id=${params?.customer_id}&page=${params?.page}&limit=${params?.limit}&invoice_search=${
          params?.search
        }&skip_log=${
          params?.skip_log ? params?.skip_log : false
        }${
          params?.sortBy ? `&sortBy=${params?.sortBy}` : ""
        }${params?.order ? `&order=${params?.order}` : ""}`,
        method: "GET",
        headers: !params?.skip_log
          ? {
            PageName: "Customer Management",
          }
          : {},
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success === true) {
          const data = rawData?.data.map((raw: any) => ({
            value: raw.id,
            ...raw,
          }));

          return { ...rawData, data };
        }
      },
    }),
    deleteCustomer: builder.mutation<DataResponse<any>, any>({
      query: (body: any) => ({
        url: `/customer-invoice/${body.code}`,
        body,
        method: "DELETE",
        headers: {
          PageName: "Customer Management",
        },
      }),
    }),
    getCustomerDetail: builder.query<DataResponse<any>, { code: string; skip_log?: boolean }>({
      query: (params) => ({
        url: `/customer-invoice/${params?.code}?skip_log=${
          params?.skip_log ? params?.skip_log : false
        }`,
        method: "GET",
        headers: !params?.skip_log
          ? {
            PageName: "Customer Management",
          }
          : {},
      }),
    }),
    getInvoiceDetail: builder.query<DataResponse<any>, { invoice_number: string; skip_log?: boolean }>({
      query: (params) => ({
        url: `/invoice/${params?.invoice_number}?skip_log=${
          params?.skip_log ? params?.skip_log : false
        }`,
        method: "GET",
        headers: !params?.skip_log
          ? {
            PageName: "Customer Management",
          }
          : {},
      }),
    }),
    createCustomer: builder.mutation<DataResponse<any>, any>({
      query: (body: any) => ({
        url: "/customer-invoice",
        body,
        method: "POST",
        headers: {
          PageName: "Customer Management",
        },
      }),
    }),
    updateCustomer: builder.mutation<DataResponse<any>, any>({
      query: (body: any) => {
        return {
          url: `/customer-invoice/${body.code}`,
          body: body,
          params: { skip_log: body?.skip_log ? body?.skip_log : false },
          method: "PUT",
          headers: {
            PageName: "Customer Management",
          },
        };
      },
    }),
    getCustomerVault: builder.query<
      DataResponse<any[]>,
      {
        code: string;
        skip_log?: boolean;
        sortBy?: string;
        order?: string;
      }
    >({
      query: (body: any) => ({
        url: `/customer-invoice/${body.code}/vault`,
        body,
        method: "GET",
        headers: {
          PageName: "Customer Management",
        },
      }),
    }),
    createCustomerVault: builder.mutation<DataResponse<any>, any>({
      query: (body: any) => ({
        url: `/customer-invoice/${body.code}/vault`,
        body,
        method: "POST",
        headers: {
          PageName: "Customer Management",
        },
      }),
    }),
    deleteCustomerVault: builder.mutation<DataResponse<any>, any>({
      query: (body: any) => ({
        url: `/customer-invoice/${body.code}/vault`,
        body,
        method: "DELETE",
        headers: {
          PageName: "Customer Management",
        },
      }),
    }),
    saveCustomerVirtualTerminal: builder.mutation<DataResponse<any>, any>({
      query: (body: any) => ({
        url: "/customer-invoice/save-by-virtual-terminal",
        body,
        method: "POST",
        headers: {
          PageName: "Customer Management",
        },
      }),
    }),
  }),
});

export const {
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useGetInvoicesQuery,
  useDeleteCustomerMutation,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useCreateCustomerVaultMutation,
  useLazyGetCustomerDetailQuery,
  useLazyGetInvoiceDetailQuery,
  useGetCustomerVaultQuery,
  useDeleteCustomerVaultMutation,
  useSaveCustomerVirtualTerminalMutation
} = customerService;
